import { reportClientError } from '@/lib/reportError';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fetchWallet } from '@/services/affiliateApi';
import { useAuthContext } from '@/contexts/AuthContext';
import { useOctOffer, startOfferCheckout, startGuestOfferCheckout, switchToYearly, claimYearlyBonus, offerPageHeadline, offerScarcityLine, offerFinePrint, creditLabel } from '@/services/offerApi';
import type { PartnerBrand } from '@/lib/partners';

/**
 * The October 2026 offer: the yearly plan with a $50 thank-you credit. Named "The Hey
 * Joe Plus October Bundle Sale" (Joe, Oct 10; it was unnamed until then) and never framed as a discount: the yearly plan is
 * $99 every day, so the only new thing is the thank-you. Dates and amounts render
 * from /api/offer/oct26, the same window the server enforces.
 *
 * One page, one action, chosen by who is reading it:
 *   new / signed out → yearly checkout      monthly → switch in place
 *   yearly           → the thank-you, once  charter / team → not offered
 */

// What Plus includes, in Joe's words from the Oct 10 offer email. Never "classes are free".
const PERKS: { title: string; detail: string }[] = [
  { title: '2 to 4 free featured events every month', detail: 'With casting directors, agents and teachers.' },
  { title: 'Member pricing on classes and courses', detail: 'Plus replays of the events you register for, while you’re a member.' },
  { title: '5 reader bookings a month with no platform fee', detail: 'You just pay the reader’s rate.' },
  { title: 'Our video editing tools', detail: 'To help you create more with the footage you shoot.' },
];
const C = { paper: '#FFFBF0', ink: '#0A0A0A', ink2: '#3A3732', muted: '#6E6A60', line: '#E8E2D2', yellow: '#FFD60A', blue: '#4F76F6' };
const GUEST_INPUT: React.CSSProperties = { width: '100%', minHeight: 52, padding: '0 16px', borderRadius: 14, border: `1.5px solid ${C.ink}`, background: '#FFFFFF', color: C.ink, fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box' as const };
const DISPLAY = "'Red Hat Display', sans-serif";
const MONO = "'Geist Mono', ui-monospace, monospace";
const RUNNER = 'https://plus.heyjoe.io/api/s3/creator-products/brand/megaphone-runner.png';
const SPEND: { title: string; detail: string }[] = [
  { title: 'Events', detail: 'Paid live events and workshops.' },
  { title: 'Classes', detail: 'On top of your member pricing.' },
  { title: 'Video token packs', detail: 'For our video editing tools.' },
];

const closesLine = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' })} at ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })} PT`;
};
const dateOnly = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');

const OfferPage: React.FC<{ brand?: PartnerBrand }> = ({ brand }) => {
  const offer = useOctOffer();
  const { toast } = useToast();
  const location = useLocation();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | { expiresAt: string | null; switched?: boolean; guest?: boolean }>(null);
  const { login } = useAuthContext();
  // Signed-out buyers: email first; name + password only when the email has no account.
  const [gEmail, setGEmail] = useState('');
  const [gFirst, setGFirst] = useState('');
  const [gLast, setGLast] = useState('');
  const [gPass, setGPass] = useState('');
  const [needsAccount, setNeedsAccount] = useState(false);
  const [gErr, setGErr] = useState<null | { msg: string; signIn?: boolean }>(null);
  const [resetSent, setResetSent] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  // Where to land after checkout: the event they came from, if it is a safe path.
  const rt = params.get('returnTo') || '';
  const returnTo = rt.startsWith('/') && !rt.startsWith('//') && !rt.includes('://') ? rt : undefined;

  // Back from Stripe after buying the year: show the "credit is in" state. The $50
  // is granted by the webhook a few seconds behind the redirect, so poll the wallet
  // briefly for the bonus and its expiry rather than showing the buy button again.
  useEffect(() => {
    if (params.get('subscription') !== 'success') return;
    // A guest bought on their account without signing in, so there is no wallet to
    // read here. Say where the year went and how to get in.
    if (params.get('guest') === '1') {
      let em = '';
      try { em = sessionStorage.getItem('oct26GuestEmail') || ''; } catch { /* storage off */ }
      setGEmail(em);
      setDone({ expiresAt: null, guest: true });
      return;
    }
    let tries = 0, stop = false;
    const look = async () => {
      if (stop) return;
      tries++;
      try {
        const w = await fetchWallet();
        const bonus = (w.bonuses || []).find(b => b.offer === 'oct26') || (w.bonuses || [])[0];
        if (bonus || tries >= 10) { setDone({ expiresAt: bonus?.expiresAt || null }); return; }
      } catch { if (tries >= 10) { setDone({ expiresAt: null }); return; } }
      setTimeout(look, 2000);
    };
    look();
    return () => { stop = true; };
  }, [params]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); }
    catch (e: any) {
      reportClientError('offer_action_failed', e.code || e.message || 'unknown', { kind: offer?.kind });
      toast({ title: e.code === 'PAYMENT_FAILED' ? 'Card declined' : 'Something went wrong', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const guestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setGErr(null);
    const email = gEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setGErr({ msg: 'Enter a valid email address.' }); return; }
    run(async () => {
      try { sessionStorage.setItem('oct26GuestEmail', email); } catch { /* storage off */ }
      if (needsAccount) {
        if (!gFirst.trim() || !gLast.trim() || gPass.length < 6) { setGErr({ msg: 'Add your first and last name and a password of 6 or more characters.' }); return; }
        const r = await fetch('/api/auth/register', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ email, password: gPass, first_name: gFirst.trim(), last_name: gLast.trim() }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.token && data.user) {
          login(data.token, data.user);
          window.location.href = await startOfferCheckout(returnTo, params.get('c') || undefined, params.get('p') || undefined);
          return;
        }
        if (data.errorType !== 'email_exists') { setGErr({ msg: data.error || 'Could not create your account. Try again.' }); return; }
        setNeedsAccount(false);
      }
      try {
        const j = await startGuestOfferCheckout(email, params.get('c') || undefined, params.get('p') || undefined);
        if (j.checkoutUrl) { window.location.href = j.checkoutUrl; return; }
        if (j.needsAccount) setNeedsAccount(true);
      } catch (err: any) {
        if (err.code === 'SIGN_IN_FIRST' || err.code === 'BAD_EMAIL') { setGErr({ msg: err.message, signIn: err.code === 'SIGN_IN_FIRST' }); return; }
        throw err;
      }
    });
  };
  const sendReset = () => run(async () => {
    const r = await fetch('/api/auth/reset-password-request', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: gEmail.trim() }) });
    const data = await r.json().catch(() => ({}));
    if (!data.success) throw new Error(data.error || 'Could not send the email.');
    setResetSent(true);
  });

  // The page is its own poster (Joe, Oct 10: the Design canvas "October Credit Sale
  // page"), so it keeps the brand paper in both site themes.
  const accent = brand ? brand.accent : C.yellow;
  const pill: React.CSSProperties = { minHeight: 56, padding: '0 30px', borderRadius: 999, border: 0, cursor: 'pointer', background: C.ink, color: C.paper, fontSize: 18, fontWeight: 700, fontFamily: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, maxWidth: '100%' };
  const body: React.CSSProperties = { fontSize: 15.5, lineHeight: 1.55, color: C.ink, margin: 0 };
  const box: React.CSSProperties = { padding: 18, borderRadius: 18, background: '#FFFFFF', border: `1.5px solid ${C.line}`, maxWidth: 560 };

  let action: React.ReactNode = null;
  if (offer) {
    if (done?.guest) {
      action = (
        <div style={box}>
          <p className="m-0 inline-flex items-center gap-2" style={{ fontSize: 16, fontWeight: 800 }}><Check className="w-4 h-4" /> You’re in.</p>
          <p style={{ ...body, marginTop: 6 }}>Your year of Hey Joe Plus and your {creditLabel(offer)} in Hey Joe credit are on {gEmail ? <strong style={{ overflowWrap: 'anywhere' }}>{gEmail}</strong> : 'the account for the email you used'}. Sign in with that email to use them.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 14 }}>
            <Link to="/auth" state={{ from: '/offer?subscription=success' }} style={pill}>Sign in</Link>
            {gEmail && !resetSent && (
              <button type="button" disabled={busy} onClick={sendReset} style={{ background: 'none', border: 0, padding: 0, color: C.ink, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 15 }}>Don’t know your password? Email me a link</button>
            )}
          </div>
          {resetSent && <p style={{ ...body, marginTop: 10 }}>Sent. Check {gEmail} for a link to set your password.</p>}
        </div>
      );
    } else if (done) {
      action = (
        <div style={box}>
          <p className="m-0 inline-flex items-center gap-2" style={{ fontSize: 16, fontWeight: 800 }}><Check className="w-4 h-4" /> {done.switched ? 'You’re on the yearly plan.' : 'Your thank-you credit is in.'}</p>
          <p style={{ ...body, marginTop: 6 }}>{creditLabel(offer)} in Hey Joe credit is in your account{done.expiresAt ? `, good until ${dateOnly(done.expiresAt)}` : ''}. Use it at checkout on any event, class or store item.</p>
          <p style={{ ...body, marginTop: 10 }}><Link to={returnTo || '/calendar'} style={{ color: C.ink, fontWeight: 700 }}>{returnTo ? 'Back to your event →' : 'See what’s coming up →'}</Link></p>
        </div>
      );
    } else if (!offer.open && offer.upcoming) {
      action = <p style={body}>This offer opens {closesLine(offer.opensAt)}.</p>;
    } else if (offer.soldOut) {
      action = <p style={body}>All {offer.cap} places in this sale have been taken. <Link to="/plans" style={{ color: C.ink, fontWeight: 700 }}>See Hey Joe Plus plans</Link>.</p>;
    } else if (!offer.open) {
      action = <p style={body}>This offer has ended. <Link to="/plans" style={{ color: C.ink, fontWeight: 700 }}>See Hey Joe Plus plans</Link>.</p>;
    } else if (offer.kind === 'signed_out') {
      action = (
        <form onSubmit={guestSubmit} style={{ maxWidth: 440, display: 'grid', gap: 10 }} noValidate>
          <input type="email" inputMode="email" autoComplete="email" placeholder="Your email" aria-label="Your email" value={gEmail}
            onChange={e => { setGEmail(e.target.value); if (needsAccount) setNeedsAccount(false); setGErr(null); }} style={GUEST_INPUT} />
          {needsAccount && (
            <>
              <p style={{ ...body, fontSize: 14.5 }}>There’s no Hey Joe account on this email yet. Add your name and a password to make one.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input autoComplete="given-name" placeholder="First name" aria-label="First name" value={gFirst} onChange={e => setGFirst(e.target.value)} style={GUEST_INPUT} />
                <input autoComplete="family-name" placeholder="Last name" aria-label="Last name" value={gLast} onChange={e => setGLast(e.target.value)} style={GUEST_INPUT} />
              </div>
              <input type="password" autoComplete="new-password" placeholder="Password (6 or more characters)" aria-label="Password" value={gPass} onChange={e => setGPass(e.target.value)} style={GUEST_INPUT} />
            </>
          )}
          <button type="submit" disabled={busy} style={{ ...pill, width: '100%' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Get the year for $99 {!busy && <ArrowRight size={18} strokeWidth={2.4} />}
          </button>
          {gErr && (
            <p style={{ ...body, fontSize: 14.5, fontWeight: 600 }}>{gErr.msg}{gErr.signIn && <> <Link to="/auth" state={{ from: `${location.pathname}${location.search}` }} style={{ color: C.ink, fontWeight: 800 }}>Sign in</Link></>}</p>
          )}
          <p style={{ ...body, fontSize: 14 }}>No need to sign in first. Have a password? <Link to="/auth" state={{ from: `${location.pathname}${location.search}` }} style={{ color: C.ink, fontWeight: 700 }}>Sign in</Link></p>
        </form>
      );
    } else if (offer.kind === 'new') {
      action = (
        <button type="button" disabled={busy} style={pill} onClick={() => run(async () => { window.location.href = await startOfferCheckout(returnTo, params.get('c') || undefined, params.get('p') || undefined); })}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Get the year for $99 {!busy && <ArrowRight size={18} strokeWidth={2.4} />}
        </button>
      );
    } else if (offer.kind === 'monthly') {
      action = confirmSwitch ? (
        <div style={box}>
          <p style={body}>We’ll charge your card on file $99 today and your year starts now. Your monthly plan stops, with no partial refund for this month.</p>
          <button type="button" disabled={busy} style={{ ...pill, marginTop: 12 }} onClick={() => run(async () => { const j = await switchToYearly(); setDone({ expiresAt: j.bonusExpiresAt, switched: true }); })}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Charge $99 and switch
          </button>
          <button type="button" onClick={() => setConfirmSwitch(false)} style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 0, color: C.ink, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>Not now</button>
        </div>
      ) : (
        <button type="button" style={pill} onClick={() => setConfirmSwitch(true)}>Switch to yearly for $99</button>
      );
    } else if (offer.kind === 'yearly') {
      action = offer.bonusClaimed
        ? <p style={body}>You’re already on the yearly plan, and your thank-you credit is in your account{offer.bonusExpiresAt ? `, good until ${dateOnly(offer.bonusExpiresAt)}` : ''}.</p>
        : offer.courtesyOpen === false
        ? <p style={body}>You’re already on the yearly plan. <Link to="/calendar" style={{ color: C.ink, fontWeight: 700 }}>See what’s coming up →</Link></p>
        : (
          <div>
            <p style={{ ...body, marginBottom: 12 }}>You’re already on the yearly plan, so there’s nothing to buy. Here’s the thank-you anyway.</p>
            <button type="button" disabled={busy} style={pill} onClick={() => run(async () => { const j = await claimYearlyBonus(); setDone({ expiresAt: j.bonusExpiresAt }); })}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add my {creditLabel(offer)} thank-you
            </button>
          </div>
        );
    } else if (offer.kind === 'team') {
      action = <p style={body}>You’re signed in with a team account, so the offer is hidden here. Sign out, or use a test account without Plus, to see the page the way a member would.</p>;
    } else if (offer.kind === 'charter') {
      action = <p style={body}>As a charter member you already have your membership, so this offer isn’t open to your account.</p>;
    } else {
      action = <p style={body}>This offer isn’t available on your account.</p>;
    }
  }

  const showBuy = !!offer && offer.open && !done && (offer.kind === 'new' || offer.kind === 'signed_out' || (offer.kind === 'monthly' && !confirmSwitch));
  // The way in for someone not ready for a year (Joe, Oct 15 2026): the 3-day trial, under every $99 button.
  const trialLine = !!offer && offer.open && !done && (offer.kind === 'new' || offer.kind === 'signed_out') ? (
    <p className="m-0" style={{ fontSize: 15, lineHeight: 1.5, color: C.ink2, marginTop: 12 }}>
      Not ready for a year? <Link to="/?code=bookit3&utm_source=plus&utm_medium=offer_page&utm_campaign=promo_trial_fallback" style={{ color: C.ink, fontWeight: 800 }}>Try Plus free for 3 days</Link>
    </p>
  ) : null;
  const h2: React.CSSProperties = { margin: '0 0 24px', fontFamily: DISPLAY, fontWeight: 900, fontSize: 'clamp(32px, 4vw, 44px)', letterSpacing: '-0.02em', color: C.ink };
  const wrap: React.CSSProperties = { maxWidth: 1180, margin: '0 auto', padding: '0 16px' };

  return (
    <main style={{ background: C.paper, color: C.ink, fontFamily: "'Geist', system-ui, sans-serif", minHeight: 'calc(100vh - 68px)' }}>
      {!offer ? (
        <div className="flex items-center justify-center" style={{ padding: '96px 0' }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: C.muted }} /></div>
      ) : (
        <>
          <div style={{ ...wrap, paddingTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            {offer.open && offer.closesAt && (
              <div style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '8px 14px', border: `1.5px solid ${C.ink}`, borderRadius: 999 }}>{creditLabel(offer)} credit ends {closesLine(offer.closesAt)}</div>
            )}
          </div>

          <section style={{ ...wrap, paddingTop: 16, paddingBottom: 48, display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'stretch' }}>
            <div style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 20 }}>
              {(brand || offer.partner) && (
                <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
                  {brand ? <img src={brand.logo} alt={brand.name} style={{ height: 30, width: 'auto', display: 'block' }} /> : <span style={{ fontSize: 15, fontWeight: 800 }}>{offer.partner!.name}</span>}
                  <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 300 }}>×</span>
                  <img src="/lovable-uploads/heyjoe-logo-black.png" alt="Hey Joe" style={{ height: 30, width: 'auto', display: 'block' }} />
                </div>
              )}
              <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: '8px 14px', background: C.ink, color: accent, borderRadius: 999, fontFamily: MONO, fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>October only · {offer.cap ?? 100} places</div>
              <h1 className="m-0" style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 'clamp(44px, 7vw, 88px)', lineHeight: 0.97, letterSpacing: '-0.03em', color: C.ink }}>
                The Hey Joe October <span style={{ background: accent, padding: '0 10px', borderRadius: 10, boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}>Credit Sale</span>
              </h1>
              <p className="m-0" style={{ fontSize: 'clamp(20px, 2.4vw, 28px)', lineHeight: 1.3, fontWeight: 600, maxWidth: 620 }}>{offerPageHeadline(offer)}</p>
              <p className="m-0" style={{ fontSize: 17, lineHeight: 1.5, maxWidth: 560, color: C.ink2 }}>{offerScarcityLine(offer)}</p>
              {brand && <p className="m-0" style={{ fontSize: 15.5, lineHeight: 1.55, maxWidth: 560, color: C.ink2 }}>{brand.intro}</p>}
              <div style={{ marginTop: 4 }}>{action}{trialLine}</div>
              {offer.open && offer.kind === 'signed_out' && (
                <p className="m-0" style={{ fontSize: 15, color: C.ink2 }}>On a monthly plan? Sign in and you can switch to yearly here.</p>
              )}
            </div>

            <div style={{ flex: '1 1 340px', minWidth: 0, position: 'relative', background: C.blue, borderRadius: 28, padding: 28, minHeight: 380, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <img src={RUNNER} alt="" aria-hidden="true" style={{ position: 'absolute', right: -10, top: 16, width: 'min(300px, 62%)', height: 'auto' }} />
              <div style={{ position: 'relative', background: accent, borderRadius: 20, padding: '22px 24px', transform: 'rotate(-3deg)', border: `2px solid ${C.ink}`, boxShadow: `0 2px 0 ${C.ink}`, maxWidth: 290 }}>
                <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Hey Joe credit</div>
                <div style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 'clamp(68px, 8vw, 96px)', lineHeight: 0.9, letterSpacing: '-0.04em', marginTop: 8 }}>{creditLabel(offer)}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 10, lineHeight: 1.35 }}>Added to your account when you join. Good for 12 months.</div>
              </div>
            </div>
          </section>

          <section style={{ background: C.ink, color: C.paper }}>
            <div style={{ ...wrap, paddingTop: 24, paddingBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 40, color: accent, letterSpacing: '-0.02em' }}>{offer.cap ?? 100}</span>
                <span style={{ fontSize: 18, fontWeight: 600 }}>places for new annual members. When they’re gone, the sale ends.</span>
              </div>
              {offer.open && typeof offer.remaining === 'number' && offer.remaining <= 75 && (
                <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: accent }}>{offer.remaining} of {offer.cap} left</div>
              )}
            </div>
          </section>

          <section style={{ ...wrap, paddingTop: 64 }}>
            <h2 style={h2}>What Plus includes</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {PERKS.map((p, i) => (
                <div key={p.title} style={{ background: '#FFFFFF', border: `1.5px solid ${C.line}`, borderRadius: 20, padding: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: C.muted }}>0{i + 1}</div>
                  <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 21, lineHeight: 1.18 }}>{p.title}</div>
                  <div style={{ fontSize: 15.5, lineHeight: 1.5, color: C.ink2 }}>{p.detail}</div>
                </div>
              ))}
            </div>
          </section>

          <section style={{ ...wrap, paddingTop: 56 }}>
            <h2 style={h2}>Spend your <span style={{ background: accent, padding: '0 8px', borderRadius: 8 }}>{creditLabel(offer)}</span> on</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {SPEND.map((u, i) => (
                <div key={u.title} style={{ border: `2px solid ${C.ink}`, borderRadius: 20, padding: 22, background: i === 2 ? '#FFFFFF' : 'transparent' }}>
                  <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24 }}>{u.title}</div>
                  <div style={{ fontSize: 15.5, lineHeight: 1.5, color: C.ink2, marginTop: 6 }}>{u.detail}</div>
                </div>
              ))}
            </div>
          </section>

          {showBuy && (
            <section style={{ ...wrap, paddingTop: 56 }}>
              <div style={{ background: accent, border: `2px solid ${C.ink}`, borderRadius: 28, padding: '32px 28px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
                <div style={{ flex: '999 1 380px', minWidth: 0 }}>
                  <div style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 40px)', lineHeight: 1.05, letterSpacing: '-0.02em' }}>$99 for the year. {creditLabel(offer)} in Hey Joe credit.</div>
                  <div style={{ fontSize: 17, marginTop: 8, fontWeight: 500 }}>{offerScarcityLine(offer)}</div>
                </div>
                <div>{action}{trialLine}</div>
              </div>
            </section>
          )}

          <footer style={{ ...wrap, paddingTop: 36, paddingBottom: 80, fontSize: 14, lineHeight: 1.6, color: C.ink2 }}>
            <p className="m-0">{offerFinePrint(offer)}</p>
            <p className="m-0" style={{ marginTop: 6 }}>Hey Joe Plus renews at $99 a year after your first year. You can cancel any time from <Link to="/billing" style={{ color: C.ink, fontWeight: 700 }}>billing</Link>.</p>
          </footer>
        </>
      )}
    </main>
  );
};

export default OfferPage;
